/**
 * The pager's index algorithm, shared by pager.ts and pager_worker.js.
 *
 * Moved here VERBATIM out of pager_worker.js. It lives in its own file for one
 * reason: the pager needs to run it on the main thread, and the worker needs to
 * keep running it too, and the one thing worse than either is two copies of a
 * sort order that have to agree.
 *
 * Every function takes the same payload the worker's message carried and returns
 * the map, mutating it in place exactly as before.
 */

export function getIndexValue(obj, path) {
    let keys = path.split('.');
    for (let key of keys) {
        if (obj.hasOwnProperty(key) && obj[key] !== '') {
            obj = obj[key];
        }
        else {
            return null;
        }
    }

    if (typeof obj === 'number') {
        // 4503599627370496
        // for precision, stringify number with 16 digits
        return obj.toString().padStart(16, '0');
    }

    return obj.toString();
}

export function insertItems(data) {
    let { items, sortBy, order, id, map, withinRange } = data;
    for (let item of items) {
        let indexValue = getIndexValue(item, sortBy);
        if (indexValue === null) {
            continue; // pass
        }

        indexValue += '\u{10FFFF}' + item[id]; // 'indexValue<sep>id'

        let low = 0;
        let high = map.length;

        while (low < high) {
            const mid = Math.floor((low + high) / 2);
            if (order === 'desc') {
                if (indexValue > map[mid]) {
                    high = mid;
                }
                else {
                    low = mid + 1;
                }
            }
            else {
                if (indexValue < map[mid]) {
                    high = mid;
                }
                else {
                    low = mid + 1;
                }
            }
        }

        // Already present? The map is sorted and this search settles AFTER any
        // equal entry, so a duplicate can only be the one just behind `low`. This
        // was a full `map.indexOf` scan, which made a single insert O(n) and a
        // folder of n files O(n^2) for no reason the sort order does not already
        // answer in O(log n).
        if (low > 0 && map[low - 1] === indexValue) {
            continue; // already has it
        }

        if ((low >= map.length && !withinRange) || low < map.length) {
            map.splice(low, 0, indexValue);
        }
    }

    return map;
}

export function deleteItem(data) {
    let { sortBy, items, id, map, list } = data;

    let target = getIndexValue(list[items[0][id]], sortBy);
    if (target === null) {
        return map;
    }

    target += '\u{10FFFF}' + items[0][id];
    let index = map.indexOf(target);
    if (index !== -1) {
        map.splice(index, 1);
    }

    return map;
}

/**
 * The 'edit' message's whole body, so the pager and the worker cannot disagree
 * about when an entry has to MOVE rather than simply be re-inserted.
 */
export function editItem(data) {
    try {
        const previousIndexValue = getIndexValue(data.list[data.items[0][data.id]], data.sortBy);
        const updatedIndexValue = getIndexValue(data.items[0], data.sortBy);

        if (previousIndexValue !== updatedIndexValue) {
            data.map = deleteItem(data);
        }
    } catch (err) {
        // no index
    }
    return insertItems(data);
}
